import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import s from './WorkTracker.module.scss';
import { personShape, taskShape } from './propTypes';
import StatusSelect from './StatusSelect';
import DescriptionEditor from './DescriptionEditor';
import Checklist from './Checklist';
import Comments from './Comments';
import { PRIORITIES } from './lib/sorting';
import { formatDateTime } from './lib/dates';

// The details panel. Mount it with key={task.id}: the editable fields are
// seeded from the task once, so a refresh from the other person never
// overwrites what is being typed. Status, checklist and comments save
// straight away; the fields save with the button.
export default function TaskDrawer({
  task, people, onClose, onStatus, onSaveFields, onSaveDescription,
  onChecklist, onAddComment, onDeleteComment, onDelete, onError,
}) {
  const [fields, setFields] = useState({
    title: task.title,
    assignee_id: task.assignee_id || '',
    due_on: task.due_on || '',
    priority: task.priority,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = async () => {
    setSaving(true);
    await onSaveFields(task, { ...fields, assignee_id: fields.assignee_id || null, due_on: fields.due_on || null });
    setSaving(false);
  };

  return (
    <>
      <div className={s.backdrop} onClick={onClose} role="presentation" />
      <aside className={s.drawer} role="dialog" aria-modal="true" aria-labelledby="wt-drawer-title">
        <div className={s.drawerHead}>
          <h2 className={s.drawerTitle} id="wt-drawer-title">{task.title}</h2>
          <button type="button" className={s.close} onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className={s.drawerBody}>
          <div className={s.inline}>
            <span className={s.label} style={{ margin: 0 }}>Status</span>
            <StatusSelect value={task.status} label="Status" onChange={(status) => onStatus(task, status)} />
          </div>

          <div className={`${s.section} ${s.fields}`}>
            <div className={s.field}>
              <label className={s.label} htmlFor="wt-title">Title</label>
              <input
                id="wt-title"
                className={s.input}
                value={fields.title}
                onChange={(e) => set('title', e.target.value)}
              />
            </div>
            <div className={s.fieldRow}>
              <div className={s.field}>
                <label className={s.label} htmlFor="wt-assignee">Assigned to</label>
                <select
                  id="wt-assignee"
                  className={s.select}
                  value={fields.assignee_id}
                  onChange={(e) => set('assignee_id', e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className={s.field}>
                <label className={s.label} htmlFor="wt-due">Due date</label>
                <input
                  id="wt-due"
                  type="date"
                  className={s.input}
                  value={fields.due_on}
                  onChange={(e) => set('due_on', e.target.value)}
                />
              </div>
              <div className={s.field}>
                <label className={s.label} htmlFor="wt-priority">Priority</label>
                <select
                  id="wt-priority"
                  className={s.select}
                  value={fields.priority}
                  onChange={(e) => set('priority', e.target.value)}
                >
                  {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          <DescriptionEditor
            key={task.id}
            task={task}
            onSave={(text, release) => onSaveDescription(task, text, release)}
            onError={onError}
          />
          <Checklist task={task} onChange={(steps) => onChecklist(task, steps)} />
          <Comments task={task} onAdd={(body) => onAddComment(task, body)} onDelete={(c) => onDeleteComment(task, c)} />

          <div className={s.drawerFoot}>
            <button type="button" className={s.btnDanger} onClick={() => onDelete(task)}>Delete</button>
            <button type="button" className={s.btnPrimary} disabled={saving || !fields.title.trim()} onClick={save}>
              Save changes
            </button>
          </div>
          <p className={s.timestamps}>
            Added
            {' '}
            {formatDateTime(task.created_at)}
            {' '}
            · Updated
            {' '}
            {formatDateTime(task.updated_at)}
            {task.completed_at && (
            <>
              {' '}
              · Completed
              {formatDateTime(task.completed_at)}
            </>
            )}
          </p>
        </div>
      </aside>
    </>
  );
}

TaskDrawer.propTypes = {
  task: taskShape.isRequired,
  people: PropTypes.arrayOf(personShape).isRequired,
  onClose: PropTypes.func.isRequired,
  onStatus: PropTypes.func.isRequired,
  onSaveFields: PropTypes.func.isRequired,
  onSaveDescription: PropTypes.func.isRequired,
  onChecklist: PropTypes.func.isRequired,
  onAddComment: PropTypes.func.isRequired,
  onDeleteComment: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};
